import PropTypes from "prop-types";
import { useCallback, useEffect, useRef, useState } from "react";
import "./RangeSlider.css";

function RangeSlider({
  min,
  max,
  step,
  rangeValue,
  onRangeValueChanged,
  className,
  disabled,
  markers = [],
  debounceMs = 0,
}) {
  const sliderRef = useRef(null);
  const containerRef = useRef(null);
  const [sliderWidth, setSliderWidth] = useState(300);
  const [isDragging, setIsDragging] = useState(false);
  const startYRef = useRef(0);
  const startValueRef = useRef(0);
  const debounceTimerRef = useRef(null);
  const pendingValueRef = useRef(null);
  const isVertical =
    className &&
    (className.includes("image-viewer-zoom-slider") ||
      className.includes("image-viewer-slider"));

  const [localValue, setLocalValue] = useState(() => {
    if (rangeValue != null && !isNaN(rangeValue)) return Number(rangeValue);
    return min != null ? Number(min) : 0;
  });

  useEffect(() => {
    if (rangeValue != null && !isNaN(rangeValue)) {
      const numValue = Number(rangeValue);
      if (Math.abs(numValue - localValue) > 0.001) setLocalValue(numValue);
    }
  }, [rangeValue, localValue]);

  const triggerChangeDebounced = useCallback(
    newValue => {
      if (debounceMs > 0) {
        pendingValueRef.current = newValue;
        if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = setTimeout(() => {
          if (onRangeValueChanged && pendingValueRef.current !== null) {
            onRangeValueChanged(pendingValueRef.current);
            pendingValueRef.current = null;
          }
        }, debounceMs);
      } else if (onRangeValueChanged) {
        onRangeValueChanged(newValue);
      }
    },
    [debounceMs, onRangeValueChanged]
  );

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        if (onRangeValueChanged && pendingValueRef.current !== null) {
          onRangeValueChanged(pendingValueRef.current);
        }
      }
    };
  }, [onRangeValueChanged]);

  useEffect(() => {
    if (sliderRef.current) {
      setSliderWidth(sliderRef.current.offsetWidth);
    }
  }, []);

  const getValueFromY = useCallback(
    clientY => {
      if (!containerRef.current) return localValue;

      const rect = containerRef.current.getBoundingClientRect();
      const relativeY = clientY - rect.top;
      const height = rect.height;

      const effectiveHeight =
        height > 50 ? height : sliderRef.current?.offsetWidth || height;
      const percentage = 1 - relativeY / effectiveHeight;
      const clampedPercentage = Math.max(0, Math.min(1, percentage));

      const newValue = min + clampedPercentage * (max - min);
      const steppedValue = Math.round(newValue / step) * step;
      return Math.max(min, Math.min(max, steppedValue));
    },
    [min, max, step, localValue]
  );

  const handleVerticalMouseDown = useCallback(
    e => {
      if (disabled || !isVertical) return;

      e.preventDefault();
      e.stopPropagation();

      setIsDragging(true);
      startYRef.current = e.clientY;
      startValueRef.current = localValue;

      const newValue = getValueFromY(e.clientY);
      setLocalValue(newValue);
      triggerChangeDebounced(newValue);
    },
    [disabled, isVertical, localValue, getValueFromY, triggerChangeDebounced]
  );

  const handleVerticalClick = useCallback(
    e => {
      if (disabled || !isVertical || !containerRef.current) return;

      e.preventDefault();
      e.stopPropagation();

      const newValue = getValueFromY(e.clientY);
      setLocalValue(newValue);
      triggerChangeDebounced(newValue);
    },
    [disabled, isVertical, getValueFromY, triggerChangeDebounced]
  );

  const handleVerticalMouseMove = useCallback(
    e => {
      if (!isDragging || !isVertical || !containerRef.current) return;
      e.preventDefault();
      e.stopPropagation();

      const clientY =
        e.clientY || (e.touches && e.touches[0] ? e.touches[0].clientY : null);
      if (clientY === null) return;

      const newValue = getValueFromY(clientY);
      setLocalValue(newValue);
      triggerChangeDebounced(newValue);
    },
    [isDragging, isVertical, getValueFromY, triggerChangeDebounced]
  );

  const handleVerticalMouseUp = useCallback(() => {
    if (isDragging) setIsDragging(false);
  }, [isDragging]);

  const handleVerticalTouchStart = useCallback(
    e => {
      if (disabled || !isVertical) return;
      e.preventDefault();
      e.stopPropagation();
      const touch = e.touches[0];
      if (!touch) return;

      setIsDragging(true);
      startYRef.current = touch.clientY;
      startValueRef.current = localValue;

      const newValue = getValueFromY(touch.clientY);
      setLocalValue(newValue);
      triggerChangeDebounced(newValue);
    },
    [disabled, isVertical, localValue, getValueFromY, triggerChangeDebounced]
  );

  useEffect(() => {
    if (!isVertical || !isDragging) return;

    document.addEventListener("mousemove", handleVerticalMouseMove);
    document.addEventListener("mouseup", handleVerticalMouseUp);
    document.addEventListener("touchmove", handleVerticalMouseMove, {
      passive: false,
    });
    document.addEventListener("touchend", handleVerticalMouseUp);

    return () => {
      document.removeEventListener("mousemove", handleVerticalMouseMove);
      document.removeEventListener("mouseup", handleVerticalMouseUp);
      document.removeEventListener("touchmove", handleVerticalMouseMove);
      document.removeEventListener("touchend", handleVerticalMouseUp);
    };
  }, [isDragging, isVertical, handleVerticalMouseMove, handleVerticalMouseUp]);

  const handleSliderChange = event => {
    if (!onRangeValueChanged || disabled || isVertical) return;
    const rawValue = event.target.value;
    const value = parseFloat(rawValue);
    if (!isNaN(value)) {
      setLocalValue(value);
      triggerChangeDebounced(value);
    }
  };

  const handleSliderInput = event => {
    if (!onRangeValueChanged || disabled || isVertical) return;
    const rawValue = event.target.value;
    const value = parseFloat(rawValue);
    if (!isNaN(value)) {
      setLocalValue(value);
      triggerChangeDebounced(value);
    }
  };

  const getMarkerPosition = value => {
    const percentage = ((value - min) / (max - min)) * 100;
    const rootStyles = getComputedStyle(document.documentElement);

    const sliderHeight = parseInt(
      rootStyles.getPropertyValue("--Slider-height")
    );
    const sliderBubbleTop = parseInt(
      rootStyles.getPropertyValue("--Slider-bubble-top")
    );
    const thumbSize = sliderHeight - sliderBubbleTop * 2;
    const thumbRadius = (thumbSize / 2 / sliderWidth) * 100;
    const adjustedPosition =
      thumbRadius + (percentage * (100 - 2 * thumbRadius)) / 100;
    const position = Math.max(
      thumbRadius,
      Math.min(100 - thumbRadius, adjustedPosition)
    );
    return position;
  };

  return (
    <div
      ref={containerRef}
      className={`range-slider-container ${
        isVertical ? "range-slider-vertical" : ""
      }`}
      onMouseDown={isVertical ? handleVerticalMouseDown : undefined}
      onClick={isVertical ? handleVerticalClick : undefined}
      onTouchStart={isVertical ? handleVerticalTouchStart : undefined}
      style={
        isVertical
          ? {
              cursor: "pointer",
              userSelect: "none",
              touchAction: "none",
            }
          : {}
      }
    >
      {/* Markers container */}
      {markers && markers.length > 0 && (
        <div className="range-slider-markers">
          {markers.map((marker, index) => (
            <div
              key={index}
              className="range-slider-marker"
              style={{
                left: `${getMarkerPosition(marker.value)}%`,
                color: marker.color || "#333",
              }}
            >
              <div
                className="marker-arrow range-slider-marker-arrow"
                style={{ borderBottomColor: marker.color || "#333" }}
              />
              <span className="marker-label">{marker.label}</span>
            </div>
          ))}
        </div>
      )}

      {/* Slider input */}
      <input
        ref={sliderRef}
        type="range"
        min={min}
        max={max}
        step={step}
        value={
          localValue != null && !isNaN(localValue)
            ? Number(localValue)
            : min != null
            ? Number(min)
            : 0
        }
        onChange={handleSliderChange}
        onInput={handleSliderInput}
        className={`App-slice-controller ${className}`}
        disabled={disabled}
        style={{
          pointerEvents: isVertical ? "none" : "auto", // Disable native events for vertical sliders
          position: "relative",
          zIndex: 100,
        }}
      />
    </div>
  );
}

RangeSlider.propTypes = {
  min: PropTypes.number,
  max: PropTypes.number,
  step: PropTypes.number,
  rangeValue: PropTypes.number,
  onRangeValueChanged: PropTypes.func,
  className: PropTypes.string,
  disabled: PropTypes.bool,
  debounceMs: PropTypes.number,
  markers: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.number.isRequired,
      color: PropTypes.string,
    })
  ),
};

export default RangeSlider;
